import { supabase } from './supabase';
import type { Shipment, ShipmentStatus } from '../mocks/shipments';
import { getCityCoords } from '../mocks/cityCoords';

function rowToShipment(row: Record<string, unknown>): Shipment {
  return {
    id: row.id as string,
    sender: row.sender as Shipment['sender'],
    receiver: {
      ...(row.receiver as Shipment['receiver']),
      email: (row.receiver_email as string) || (row.receiver as Record<string, string>)?.email,
    },
    status: row.status as ShipmentStatus,
    weight: Number(row.weight),
    dimensions: (row.dimensions as string) ?? '',
    service: (row.service as string) ?? '',
    carrier: (row.carrier as string) ?? 'SwiftTrack',
    estimatedDelivery: (row.estimated_delivery as string) ?? '',
    origin: row.origin as Shipment['origin'],
    destination: row.destination as Shipment['destination'],
    currentLocation: (row.current_location as Shipment['currentLocation']) ?? undefined,
    timeline: (row.timeline as Shipment['timeline']) ?? [],
    description: (row.description as string) ?? '',
    value: Number(row.value),
    paymentMethod: (row.payment_method as string) ?? undefined,
    note: (row.note as string) ?? undefined,
    reason: (row.reason as string) ?? undefined,
  };
}

function shipmentToRow(s: Shipment) {
  return {
    id: s.id,
    sender: s.sender,
    receiver: s.receiver,
    receiver_email: s.receiver?.email ?? null,
    status: s.status,
    weight: s.weight,
    dimensions: s.dimensions,
    service: s.service,
    carrier: s.carrier,
    estimated_delivery: s.estimatedDelivery,
    origin: s.origin,
    destination: s.destination,
    current_location: s.currentLocation ?? null,
    timeline: s.timeline,
    description: s.description,
    value: s.value,
    payment_method: s.paymentMethod ?? null,
    note: s.note ?? null,
    reason: s.reason ?? null,
  };
}

export async function fetchShipmentById(id: string): Promise<Shipment | null> {
  const { data, error } = await supabase
    .from('shipments')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error || !data) return null;
  return rowToShipment(data as Record<string, unknown>);
}

export async function fetchAllShipments(): Promise<Shipment[]> {
  const { data, error } = await supabase
    .from('shipments')
    .select('*')
    .order('created_at', { ascending: false });
  if (error || !data) return [];
  return (data as Record<string, unknown>[]).map(rowToShipment);
}

export async function createShipment(shipment: Shipment): Promise<{ error: string | null }> {
  let resolved = { ...shipment };
  if (!resolved.origin.lat || !resolved.origin.lng) {
    const coords = getCityCoords(`${shipment.origin.city}, ${shipment.origin.country}`) ?? getCityCoords(shipment.origin.city);
    if (coords) resolved = { ...resolved, origin: { ...resolved.origin, lat: coords[0], lng: coords[1] } };
  }
  if (!resolved.destination.lat || !resolved.destination.lng) {
    const coords = getCityCoords(`${shipment.destination.city}, ${shipment.destination.country}`) ?? getCityCoords(shipment.destination.city);
    if (coords) resolved = { ...resolved, destination: { ...resolved.destination, lat: coords[0], lng: coords[1] } };
  }
  const { error } = await supabase.from('shipments').insert(shipmentToRow(resolved));
  return { error: error ? error.message : null };
}

export async function updateShipment(
  id: string,
  updates: Partial<Shipment> & { currentLocationStr?: string }
): Promise<{ error: string | null }> {
  const dbUpdates: Record<string, unknown> = {};
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.note !== undefined) dbUpdates.note = updates.note ?? null;
  if (updates.reason !== undefined) dbUpdates.reason = updates.reason ?? null;
  if (updates.timeline !== undefined) dbUpdates.timeline = updates.timeline;

  // Resolve current location string to coordinates
  if (updates.currentLocationStr) {
    const str = updates.currentLocationStr.trim();
    const coords = getCityCoords(str) ?? getCityCoords(str.split(',')[0].trim());
    if (coords) {
      dbUpdates.current_location = { lat: coords[0], lng: coords[1], city: str };
    } else {
      dbUpdates.current_location = { lat: 0, lng: 0, city: str };
    }
  }

  const { error } = await supabase.from('shipments').update(dbUpdates).eq('id', id);
  return { error: error ? error.message : null };
}

export function generateTrackingId(): string {
  const year = new Date().getFullYear();
  const num = String(Math.floor(10000 + Math.random() * 89999)).padStart(6, '0');
  return `SWT-${year}-${num}`;
}

export async function deleteShipment(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('shipments').delete().eq('id', id);
  return { error: error ? error.message : null };
}

export async function updateShipmentAll(
  id: string,
  shipment: Partial<Shipment>
): Promise<{ error: string | null }> {
  const updates: Record<string, unknown> = {};

  if (shipment.sender !== undefined) updates.sender = shipment.sender;
  if (shipment.receiver !== undefined) {
    updates.receiver = shipment.receiver;
    updates.receiver_email = shipment.receiver?.email ?? null;
  }
  if (shipment.status !== undefined) updates.status = shipment.status;
  if (shipment.weight !== undefined) updates.weight = shipment.weight;
  if (shipment.dimensions !== undefined) updates.dimensions = shipment.dimensions;
  if (shipment.service !== undefined) updates.service = shipment.service;
  if (shipment.carrier !== undefined) updates.carrier = shipment.carrier;
  if (shipment.estimatedDelivery !== undefined) updates.estimated_delivery = shipment.estimatedDelivery;
  if (shipment.description !== undefined) updates.description = shipment.description;
  if (shipment.value !== undefined) updates.value = shipment.value;
  if (shipment.paymentMethod !== undefined) updates.payment_method = shipment.paymentMethod ?? null;
  if (shipment.note !== undefined) updates.note = shipment.note ?? null;
  if (shipment.reason !== undefined) updates.reason = shipment.reason ?? null;
  if (shipment.timeline !== undefined) updates.timeline = shipment.timeline;

  if (shipment.origin !== undefined) {
    let origin = { ...shipment.origin };
    if (!origin.lat || !origin.lng) {
      const coords = getCityCoords(`${origin.city}, ${origin.country}`) ?? getCityCoords(origin.city);
      if (coords) origin = { ...origin, lat: coords[0], lng: coords[1] };
    }
    updates.origin = origin;
  }

  if (shipment.destination !== undefined) {
    let dest = { ...shipment.destination };
    if (!dest.lat || !dest.lng) {
      const coords = getCityCoords(`${dest.city}, ${dest.country}`) ?? getCityCoords(dest.city);
      if (coords) dest = { ...dest, lat: coords[0], lng: coords[1] };
    }
    updates.destination = dest;
  }

  if (shipment.currentLocation !== undefined) {
    updates.current_location = shipment.currentLocation ?? null;
  }

  const { error } = await supabase.from('shipments').update(updates).eq('id', id);
  return { error: error ? error.message : null };
}